import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { es } from "date-fns/locale";
import "./DatePickerCalendar.css";

const WEEKDAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];

/**
 * @param {object} props
 * @param {string} props.selectedISO - currently selected day (ISO string)
 * @param {(iso: string) => void} props.onSelect - called with ISO when a day is picked
 * @param {Set<string>|string[]} [props.markedDates] - YYYY-MM-DD days that have matches
 * @param {(monthStart: Date) => void} [props.onMonthChange] - fired when visible month changes
 * @param {() => void} [props.onClose]
 */
const DatePickerCalendar = ({
  selectedISO,
  onSelect,
  markedDates,
  onMonthChange,
  onClose,
}) => {
  const selected = useMemo(() => {
    try {
      return selectedISO ? parseISO(selectedISO.split("T")[0]) : new Date();
    } catch {
      return new Date();
    }
  }, [selectedISO]);

  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected));
  const panelRef = useRef(null);

  useEffect(() => {
    setViewMonth(startOfMonth(selected));
  }, [selected]);

  useEffect(() => {
    onMonthChange?.(viewMonth);
    // Only re-fire when the visible month changes, not when callback identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMonth]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const marked = useMemo(() => {
    if (!markedDates) return new Set();
    return markedDates instanceof Set ? markedDates : new Set(markedDates);
  }, [markedDates]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  const shiftMonth = (delta) => {
    setViewMonth((m) => addMonths(m, delta));
  };

  const pickDay = (day) => {
    const iso = `${format(day, "yyyy-MM-dd")}T12:00:00.000Z`;
    onSelect(iso);
    onClose?.();
  };

  return (
    <div className="date-picker" ref={panelRef} role="dialog" aria-label="Calendario">
      <div className="date-picker-header">
        <button
          type="button"
          className="date-picker-nav"
          onClick={() => shiftMonth(-1)}
          aria-label="Mes anterior"
        >
          ‹
        </button>
        <div className="date-picker-title">
          {format(viewMonth, "MMMM yyyy", { locale: es })}
        </div>
        <button
          type="button"
          className="date-picker-nav"
          onClick={() => shiftMonth(1)}
          aria-label="Mes siguiente"
        >
          ›
        </button>
      </div>

      <div className="date-picker-weekdays">
        {WEEKDAYS.map((d) => (
          <div key={d} className="date-picker-weekday">
            {d}
          </div>
        ))}
      </div>

      <div className="date-picker-grid">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const inMonth = isSameMonth(day, viewMonth);
          const selectedDay = isSameDay(day, selected);
          const today = isToday(day);
          const hasGames = marked.has(key);

          return (
            <button
              key={key}
              type="button"
              className={[
                "date-picker-day",
                inMonth ? "" : "outside",
                selectedDay ? "selected" : "",
                today ? "today" : "",
                hasGames ? "has-games" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => pickDay(day)}
              aria-label={format(day, "d MMMM yyyy", { locale: es })}
              aria-pressed={selectedDay}
            >
              <span>{format(day, "d")}</span>
              {hasGames && <span className="date-picker-dot" aria-hidden />}
            </button>
          );
        })}
      </div>

      <div className="date-picker-footer">
        <button
          type="button"
          className="date-picker-today-btn"
          onClick={() => {
            const now = new Date();
            pickDay(now);
          }}
        >
          Hoy
        </button>
      </div>
    </div>
  );
};

export default DatePickerCalendar;
